import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Step, Stepper } from '@freelancelabs/components';

import RefusalNotice from '../../../components/RefusalNotice';
import { InviteUserDocument, RolesDocument } from '../../../graphql/__generated__/types';
import AttachmentsStep from './AttachmentsStep';
import DashboardsStep from './DashboardsStep';
import IdentityStep from './IdentityStep';
import PartnerListStep from './PartnerListStep';
import ReviewStep from './ReviewStep';
import { Draft, STEP_COUNT, applyPatch, emptyDraft, stepBlocker } from './draft';
import { inviteRefusalOf } from './inviteRefusal';

/**
 * Onboarding a portal user. Five steps where the mockup has seven. Steps 3 and 4 never block and
 * show an empty catalogue until each catalogue has a writer. Everything is sent in one mutation.
 */
const OnboardingPage = () => {
    const navigate = useNavigate();
    // The address seeded when arriving from "Add an account" on an existing sheet: the same journey,
    // just primed. No second mail: inviteUser sends nothing to an already-active person.
    const location = useLocation();
    const addAccountEmail = (location.state as { addAccountEmail?: string } | null)?.addAccountEmail ?? '';
    const addingAccount = addAccountEmail.length > 0;

    const [step, setStep] = useState(1);
    // Whether the review has been reached once: what makes "Modifier" a round trip.
    const [reviewed, setReviewed] = useState(false);
    const [draft, setDraft] = useState<Draft>(() => emptyDraft(addAccountEmail));
    const [inviteUser, { data, loading, error, reset }] = useMutation(InviteUserDocument);

    // Roles read here too (from cache): the footer must refuse a role that stops being grantable
    // while on the review, where step 2 is not even mounted.
    const { data: rolesData } = useQuery(RolesDocument);
    const roles = (rolesData?.portal ?? []).filter(Boolean);

    // Never a bare spread: correcting the address must drop the previous suggestion's markets. See applyPatch.
    const patch = (values: Partial<Draft>) => setDraft((current) => applyPatch(current, values));
    const blocker = stepBlocker(step, draft, roles);

    const goToStep = (next: number) => {
        if (next === STEP_COUNT) {
            setReviewed(true);
        }
        setStep(next);
    };

    const result = data?.inviteUser;

    const submit = async () => {
        // No contact, no send: contactId is ID!, so this guard makes the rule an invariant, not an appearance.
        if (!draft.contact) {
            return;
        }
        await inviteUser({
            variables: {
                input: {
                    email: draft.email.trim(),
                    // The contact picked at step 1 travels with the invitation; the API writes
                    // contact_onboarding in the same transaction as the attachments.
                    contactId: draft.contact.id,
                    // The picked contact's identity, guaranteed to belong to this address (applyPatch
                    // nulls it on change). Null rather than '' so the API keeps what it already holds.
                    firstName: draft.contact.firstName,
                    lastName: draft.contact.lastName,
                    function: draft.contact.jobFunction,
                    attachments: draft.attachments.map((attachment) => ({
                        organizationId: attachment.organizationId,
                        roleId: attachment.roleId as string,
                        dataScope: attachment.dataScope,
                        // Activities and never a market, even when a whole market was ticked: the
                        // market is just a box that ticks several.
                        needIds: attachment.needIds,
                        serviceIds: attachment.serviceIds,
                    })),
                    // Absence is absence: null means the operator granted nothing, no third state.
                    dashboardIds: draft.dashboards.length > 0 ? draft.dashboards.map((entry) => entry.id) : null,
                    partnerListId: draft.partnerListId,
                },
            },
        });
    };

    if (result?.userId) {
        return (
            <section className="flex max-w-2xl flex-col gap-4">
                <h1 className="text-xl font-semibold text-text-900">{c('Onboarding done').t`Onboarding recorded`}</h1>
                <Alert type={result.activationEmailSent ? 'success' : 'warning'}>
                    {result.activationEmailSent
                        ? c('Onboarding done')
                              .t`The activation email has left for ${draft.email}. The person stays « invited » until they set their password.`
                        : c('Onboarding done')
                              .t`The attachments are recorded, but no email left: either the person already had an active account, or the send failed and sits in the retry queue.`}
                </Alert>
                <div className="flex flex-wrap gap-2">
                    <Button category="default" onClick={() => navigate(`/utilisateurs/${result.userId}`)}>
                        {c('Onboarding action').t`Open the user sheet`}
                    </Button>
                    {/* reset() is what actually leaves this screen: the confirmation renders on
                        Apollo's data?.inviteUser, which survives the setters below. */}
                    <Button
                        category="weak"
                        onClick={() => {
                            reset();
                            setDraft(emptyDraft());
                            setStep(1);
                            setReviewed(false);
                        }}
                    >
                        {c('Onboarding action').t`Onboard another person`}
                    </Button>
                </div>
            </section>
        );
    }

    const steps: Step[] = [
        { name: c('Onboarding step').t`Identification`, state: step === 1 ? 'current' : 'done' },
        {
            name: c('Onboarding step').t`Accounts, roles and perimeters`,
            state: step === 2 ? 'current' : step > 2 ? 'done' : 'upcoming',
        },
        {
            name: c('Onboarding step').t`Dashboards`,
            state: step === 3 ? 'current' : step > 3 ? 'done' : 'upcoming',
        },
        {
            name: c('Onboarding step').t`Partners`,
            state: step === 4 ? 'current' : step > 4 ? 'done' : 'upcoming',
        },
        { name: c('Onboarding step').t`Review and invite`, state: step === 5 ? 'current' : 'upcoming' },
    ];

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">
                        {addingAccount ? c('Onboarding').t`Add an account` : c('Onboarding').t`Onboard a user`}
                    </h1>
                    {addingAccount ? (
                        <p className="mt-1 text-xs text-text-600">
                            {c('Onboarding')
                                .t`Adding an account to ${addAccountEmail}. Search this address to find them, then pick the account to add: no second person is created, and no email is sent if they already activated their account.`}
                        </p>
                    ) : (
                        <p className="mt-1 text-xs text-text-600">{c('Onboarding').t`Step ${step} of ${STEP_COUNT}`}</p>
                    )}
                </div>
                <Link to="/utilisateurs" className="ml-auto">
                    <Button category="weak">{c('Onboarding action').t`Cancel`}</Button>
                </Link>
            </header>

            <div className="grid gap-6 lg:grid-cols-[240px_1fr] lg:items-start">
                <Stepper steps={steps} caption={c('Onboarding').t`Onboarding steps`} />

                <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-border-norm bg-background-norm px-4 py-4">
                        {step === 1 && <IdentityStep draft={draft} onChange={patch} />}
                        {step === 2 && (
                            <AttachmentsStep draft={draft} onChange={patch} onEditIdentity={() => setStep(1)} />
                        )}
                        {step === 3 && <DashboardsStep draft={draft} onChange={patch} />}
                        {step === 4 && <PartnerListStep draft={draft} onChange={patch} />}
                        {step === 5 && <ReviewStep draft={draft} onEditStep={setStep} />}
                    </div>

                    {/* These refusals name a state of the data, not a screen failure: inviteRefusalOf
                        sorts the codes into four families, each with its own gesture. */}
                    {error && <RefusalNotice refusal={inviteRefusalOf(error)} />}

                    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-norm bg-background-norm px-4 py-3">
                        <Button category="weak" disabled={step === 1} onClick={() => setStep(step - 1)}>
                            {c('Onboarding action').t`Previous`}
                        </Button>
                        {/* Why the button refuses, next to the button. */}
                        <span className="flex-1 text-sm text-text-600">{blocker ?? ''}</span>
                        {reviewed && step < STEP_COUNT && (
                            <Button category="weak" disabled={Boolean(blocker)} onClick={() => goToStep(STEP_COUNT)}>
                                {c('Onboarding action').t`Back to the review`}
                            </Button>
                        )}
                        {step < STEP_COUNT ? (
                            <Button category="default" disabled={Boolean(blocker)} onClick={() => goToStep(step + 1)}>
                                {c('Onboarding action').t`Continue`}
                            </Button>
                        ) : (
                            // Refused as well as explained: the review re-runs step 2's checks, which
                            // a later edit can break (a stale suggestion, or a role no longer grantable).
                            <Button category="default" loading={loading} disabled={Boolean(blocker)} onClick={submit}>
                                {c('Onboarding action').t`Send the invitation`}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OnboardingPage;
